import React, { useState, useEffect } from 'react';
import DynamicForm from './components/DynamicForm';
import PaymentStep from './components/PaymentStep';
import { fetchCamps, fetchFormStructure, submitRegistration } from './services/api';

const formatText = (text) => {
  if (!text) return { __html: '' };
  const html = String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/__(.*?)__/g, '<u>$1</u>')
    .replace(/\\n/g, '<br />') // Fångar om du bokstavligen skriver \n i cellen
    .replace(/\n/g, '<br />'); // Fångar osynliga alt+enter radbrytningar
  return { __html: html };
};

const isPaymentEnabled = (camp) => {
  if (!camp) return false;

  const flag = camp.paymentEnabled;
  if (flag !== undefined && flag !== null && flag !== '') {
    return (
      flag === true ||
      flag === 1 ||
      String(flag).trim().toUpperCase() === 'TRUE'
    );
  }

  return Number(camp.price) > 0 && Boolean(String(camp.swishNumber || '').trim());
};

const App = () => {
  const [step, setStep] = useState('SELECT_CAMP');
  const [camps, setCamps] = useState([]);
  const [selectedCampId, setSelectedCampId] = useState('');
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [formStructure, setFormStructure] = useState(null);
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingForm, setIsLoadingForm] = useState(false);

  const Separator = () => (
    <span className="text-gray-400 font-light select-none">&nbsp;/&nbsp;</span>
  );

  useEffect(() => {
    const loadCamps = async () => {
      try {
        const apiCamps = await fetchCamps();
        setCamps(apiCamps);
      } catch (error) {
        console.error("Failed to load camps", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadCamps();
  }, []);

  useEffect(() => {
    if (selectedCampId) {
      const camp = camps.find(c => String(c.id) === String(selectedCampId));
      setSelectedCamp(camp || null);
    } else {
      setSelectedCamp(null);
    }
  }, [selectedCampId, camps]);

  const resetFlow = () => {
    setStep('SELECT_CAMP');
    setSelectedCamp(null);
    setSelectedCampId('');
    setFormStructure(null);
    setFormData({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCampSelect = async () => {
    if (!selectedCamp || isLoadingForm) return;
    setIsLoadingForm(true);
    try {
      const structure = await fetchFormStructure(selectedCamp.googleFormId);
      if (structure) {
        setFormStructure(structure);
        setStep('FILL_FORM');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        alert("Kunde inte hämta formuläret.");
      }
    } catch (error) {
      alert("Ett fel uppstod.");
    } finally {
      setIsLoadingForm(false);
    }
  };

  const handleFormSubmit = async (answers) => {
    setIsSubmitting(true);
    try {
      const success = await submitRegistration(selectedCampId, answers);
      if (success) {
        setFormData(answers);
        setStep(isPaymentEnabled(selectedCamp) ? 'PAYMENT' : 'DONE');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        alert("Något gick fel vid inskickningen.");
      }
    } catch (error) {
      alert("Anslutningsfel.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentComplete = () => {
    setStep('SUCCESS');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const campDisplayName = selectedCamp?.displayName || selectedCamp?.name || '';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-['Nunito']">
      {/* STICKY HEADER */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 py-5 sm:py-6 px-4 sm:px-6 shadow-sm mb-4 sm:mb-8">
        <div className="max-w-4xl mx-auto w-full flex flex-col items-center">
          <div className="w-full px-4 sm:px-0 flex justify-center items-center">
            <img
              src="/logo.png"
              alt="Svedala Scoutkår"
              className="max-h-16 sm:max-h-20 w-auto max-w-full object-contain cursor-pointer"
              onClick={resetFlow}
            />
          </div>
          <h1
            className="mt-2 font-bold text-[#003360] tracking-[0.2em] uppercase text-center"
            style={{ fontSize: 'clamp(0.8rem, 4vw, 1.25rem)' }}
          >
            Anmälan till hajk
          </h1>
        </div>
      </header>

      <main className={`flex-1 mx-auto w-full px-6 pb-16 ${step === 'PAYMENT' || step === 'SUCCESS' ? 'max-w-md' : 'max-w-2xl'}`}>

        {/* ── BREADCRUMB ── */}
        {step !== 'SELECT_CAMP' && (
          <nav className="flex px-4 py-2 text-[#003360] mb-8" aria-label="Breadcrumb">
            <ol className="flex flex-row items-center list-none p-0 m-0 text-sm sm:text-base font-medium gap-2">
              <li className="flex items-center">
                <button
                  onClick={resetFlow}
                  className="text-[#003360] font-bold px-3 py-1.5 rounded-md transition-colors duration-200 hover:bg-blue-100 hover:text-blue-900 cursor-pointer"
                >
                  Start
                </button>
              </li>

              <Separator />

              <li className="text-[#003360] font-semibold truncate bg-blue-50/50 px-3 py-1.5 rounded-md">
                {campDisplayName}
              </li>
            </ol>
          </nav>
        )}

        {/* ── STEP 1: SELECT CAMP ── */}
        {step === 'SELECT_CAMP' && (
          <div className="bg-white p-8 sm:p-10 rounded-2xl shadow-sm border border-gray-100">
            <label htmlFor="camp-select" className="block text-lg font-bold text-[#003360] mb-4">Välj arrangemang</label>

            {isLoading ? (
              <div className="flex flex-col items-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#003360] mb-4"></div>
                <p className="text-gray-400 text-sm italic">Hämtar tillgängliga arrangemang...</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="relative">
                  <select
                    id="camp-select"
                    className="w-full appearance-none p-4 pr-12 border border-gray-200 rounded-xl text-base font-['Nunito'] truncate focus:border-[#003360] focus:ring-1 focus:ring-[#003360] outline-none transition-all cursor-pointer bg-white"
                    onChange={(e) => setSelectedCampId(e.target.value)}
                    value={selectedCampId}
                  >
                    <option value="" className="font-['Nunito']">-- Välj arrangemang --</option>
                    {camps.map(camp => (
                      <option key={camp.id} value={camp.id} className="font-['Nunito']">{camp.displayName || camp.name}</option>
                    ))}
                  </select>

                  {/* ritar en svg-pil och flyttar från kanten */}
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#003360]"
                  >
                    <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>

                {/* Camp info card */}
                {selectedCamp && (
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 sm:p-8 animate-in fade-in slide-in-from-bottom-4 duration-500 shadow-sm mt-2">
                    <div className="flex flex-col mb-2">
                      <h3 className="font-bold text-[#003360] text-xl sm:text-2xl mb-3">{campDisplayName}</h3>
                      {selectedCamp.description && (
                        <p
                          className="text-gray-600 text-sm sm:text-base leading-relaxed mb-4 whitespace-pre-wrap"
                          dangerouslySetInnerHTML={formatText(selectedCamp.description)}
                        />
                      )}
                      {selectedCamp.price > 0 && (
                        <span className="text-[#003360] font-bold text-lg sm:text-xl">
                          {selectedCamp.price} kr
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Button */}
                {selectedCampId && (
                  <button
                    onClick={handleCampSelect}
                    disabled={isLoadingForm}
                    className="w-full py-4 rounded-xl font-bold text-lg transition-all bg-[#003360] text-white hover:bg-blue-800 disabled:opacity-50 shadow-sm active:scale-[0.98] mt-2"
                  >
                    {isLoadingForm ? 'Laddar formulär...' : 'Gå vidare till anmälan'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}


        {/* ── STEP 2: FILL FORM (Separate Cards) ── */}
        {step === 'FILL_FORM' && (
          <div className="flex flex-col">
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-8 sm:p-12 mb-3">
              <h2 className="text-xl sm:text-2xl font-bold text-[#003360] mb-0">{campDisplayName}</h2>
              <p className="text-base text-gray-600">
                Fyll i uppgifterna nedan för att anmäla dig till arrangemanget. <br></br>Fält markerade med <span className="text-red-500 font-bold">*</span> är obligatoriska.
              </p>
            </div>

            <DynamicForm
              structure={formStructure}
              campName={campDisplayName}
              onSubmit={handleFormSubmit}
              isSubmitting={isSubmitting}
            />
          </div>
        )}

        {/* ── STEP 3: PAYMENT ── */}
        {step === 'PAYMENT' && (
          <div className="space-y-8 px-1">
            <PaymentStep
              camp={selectedCamp}
              participantName={Object.values(formData)[0] || 'Deltagare'}
              onPaymentComplete={handlePaymentComplete}
            />
          </div>
        )}

                {step === 'DONE' && (
          <div className="bg-green-50 rounded-xl shadow-sm border border-green-200 p-8 sm:p-16 text-center animate-in zoom-in-95 duration-500">
            <p className="text-[#003360] text-lg mb-10 leading-relaxed">
              Din anmälan till <span className="font-bold">{campDisplayName}</span> är registrerad.
              <br />
              Ingen betalning behövs på den här sidan.
            </p> 
            <br></br>
            <button
              onClick={resetFlow}
              className="w-full py-4 rounded-xl text-lg font-bold text-white bg-[#003360] hover:bg-blue-800 transition-all shadow-sm active:scale-95"
            >
              Tillbaka till start
            </button>
          </div>
        )}

        {/* ── STEP 4: SUCCESS ── */}
        {step === 'SUCCESS' && (
          <div className="bg-green-50 rounded-xl shadow-sm border border-green-200 p-8 sm:p-16 text-center animate-in zoom-in-95 duration-500">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#003360] mb-3">Anmälan mottagen!</h2>
            <p className="text-[#003360] text-lg mb-10 leading-relaxed">Tack för din anmälan till <span className="font-bold">{campDisplayName}</span>.</p>
            <button
              onClick={resetFlow}
              className="w-full py-4 rounded-xl text-lg font-bold text-white bg-[#003360] hover:bg-blue-800 transition-all shadow-sm active:scale-95"
            >
              Tillbaka till start
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default App;
